import useUser from 'hooks/useUser';
import { getSocket } from 'lib/socket';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  notificationApi,
  useDeleteNotificationMutation,
  useMarkAsReadMutation,
} from 'redux/features/notificationApi';

import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import MarkdownWord from './MarkdownWord';

const NotificationSystem = ({ getNotification }) => {
  const user = useUser();
  const dispatch = useDispatch();

  const socket = getSocket();

  const navigate = useNavigate();

  const handleNotificationDoubleClick = (notif) => {
    if (notif.title === 'Log Update Request') {
      navigate('/admin-request-management');
    } else if (
      notif.type === 'LEAVE_REQUEST_CREATED' ||
      notif.type === 'LEAVE_REQUEST_APPROVED' ||
      notif.type === 'LEAVE_REQUEST_REJECTED' ||
      notif.type === 'LEAVE_REQUEST_UPDATED'
    ) {
      const status =
        notif.type === 'LEAVE_REQUEST_CREATED'
          ? 'PENDING'
          : notif.type === 'LEAVE_REQUEST_APPROVED'
            ? 'APPROVED'
            : 'REJECTED';
      navigate(
        `/${user?.role === 'ADMIN' ? 'admin-leave-management' : 'employee-requests'}?id=${notif.id}&tab=leave&subTab=${status}`,
      );
    }
  };
  useEffect(() => {
    if (!socket) return;
    const handler = (payload) => {
      const method = user?.role === 'ADMIN' ? 'getNotification' : 'getNotificationByUser';
      dispatch(
        notificationApi.util.updateQueryData(method, undefined, (draft) => {
          draft.unshift(payload);
        }),
      );
    };
    socket.on('adminNotification', handler);
    socket.on('userNotification', handler);
    return () => {
      socket.off('adminNotification', handler);
      socket.off('userNotification', handler);
    };
  }, [socket, dispatch, user?.role]);

  const [showNotifications, setShowNotifications] = useState(false);
  const [visibleNotifications, setVisibleNotifications] = useState(false); // for smooth animation
  const [hoveredNotificationId, setHoveredNotificationId] = useState(null);
  const { data: notifications } = getNotification();

  const notificationConfig = {
    LEAVE_REQUEST_APPROVED: {
      icon: 'CheckCircle',
      iconBg: 'bg-success-50',
      iconColor: '#10B981',
    },
    APPROVED: {
      icon: 'CheckCircle',
      iconBg: 'bg-success-50',
      iconColor: '#10B981',
    },
    REJECTED: {
      icon: 'X',
      iconBg: 'bg-error-50',
      iconColor: '#EF4444',
    },
    CREATED: {
      icon: 'AlertCircle',
      iconBg: 'bg-primary-50',
      iconColor: '#3B82F6',
    },
    LEAVE_REQUEST_CREATED: { icon: 'Calendar', iconBg: 'bg-warning-50', iconColor: '#F59E0B' },
    LEAVE_REQUEST_REJECTED: { icon: 'X', iconBg: 'bg-error-50', iconColor: '#EF4444' },
    default: { icon: 'Bell', iconBg: 'bg-secondary-100', iconColor: '#64748B' },
  };

  const unreadCount = notifications?.filter((n) => !n.is_read).length;
  const [markAllAsRead] = useMarkAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  const handleNotificationClick = async (id) => {
    await markAllAsRead([id]);
  };

  const handleDeleteNotification = async (id, e) => {
    e.stopPropagation();
    await deleteNotification([id]);
  };

  const markAllAsReadAll = async () =>
    await markAllAsRead(notifications?.filter((item) => !item?.is_read)?.map((notif) => notif?.id));

  const clearAllNotifications = async () => {
    await deleteNotification(notifications?.map((notif) => notif?.id));
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return notifTime.toLocaleDateString();
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notification-container')) {
        setVisibleNotifications(false); // start closing animation
        setTimeout(() => setShowNotifications(false), 300); // hide after animation
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  // Handle opening animation
  useEffect(() => {
    if (showNotifications) {
      setVisibleNotifications(true);
    }
  }, [showNotifications]);

  return (
    <div className="relative notification-container">
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative w-10 h-10 flex items-center justify-center rounded-lg hover:bg-secondary-100 transition-all duration-200 group"
        title="Notifications"
      >
        <Icon
          name="Bell"
          size={20}
          className="group-hover:scale-110 transition-transform duration-200"
        />
        {unreadCount > 0 && (
          <>
            <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-error animate-pulse"></span>
            <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-error text-white text-[10px] font-bold rounded-full">
              {unreadCount > 50 ? '50+' : unreadCount}
            </span>
          </>
        )}
      </button>

      {/* Notification Dropdown */}
      {showNotifications && (
        <div
          className={`absolute right-0 mt-2 w-96 bg-white border border-border rounded-xl shadow-2xl z-50 overflow-hidden transform transition-all duration-300 ${
            visibleNotifications
              ? 'opacity-100 translate-y-0 scale-100'
              : 'opacity-0 -translate-y-4 scale-95'
          }`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-4 border-b border-border flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Icon name="Bell" size={18} color="#3B82F6" />
              <h4 className="text-base font-semibold text-text-primary">Notifications</h4>
            </div>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsReadAll}
                  className="text-xs text-primary hover:text-primary-600 font-medium flex items-center space-x-1"
                >
                  <Icon name="CheckCheck" size={14} />
                  <span>Mark all read</span>
                </button>
              )}
              {notifications?.length > 0 && (
                <button
                  onClick={clearAllNotifications}
                  className="text-xs text-secondary-500 hover:text-error transition-colors font-medium flex items-center space-x-1"
                >
                  <Icon name="Trash2" size={14} />
                  <span>Clear all</span>
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
            {notifications?.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-full flex items-center justify-center">
                  <Icon name="BellOff" size={32} color="#94A3B8" />
                </div>
                <p className="text-secondary-600 text-sm font-semibold">All caught up!</p>
                <p className="text-secondary-400 text-xs mt-1">No notifications at the moment</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications?.map((notif) => {
                  const config = notificationConfig[notif.type] || notificationConfig.default;
                  return (
                    <div
                      key={notif.id}
                      className={`relative p-4 cursor-pointer transition-all duration-300 group
                        ${!notif.is_read ? 'bg-primary-50/30' : 'bg-white'}
                        hover:bg-secondary-50`}
                      onClick={(e) => !notif.is_read && handleNotificationClick(notif.id, e)}
                      onMouseEnter={() => setHoveredNotificationId(notif.id)}
                      onMouseLeave={() => setHoveredNotificationId(null)}
                      title={'Double click to view details'}
                      onDoubleClick={() => handleNotificationDoubleClick(notif)}
                    >
                      <div className="flex space-x-3">
                        <div
                          className={`w-10 h-10 rounded-lg ${config.iconBg} flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110`}
                        >
                          <Icon name={config.icon} size={20} color={config.iconColor} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 pr-2">
                              <p
                                className={`text-sm ${
                                  !notif.is_read ? 'font-semibold' : 'font-medium'
                                } text-text-primary line-clamp-1`}
                              >
                                {notif.title}
                              </p>
                              <div className="text-xs text-text-secondary mt-0.5 line-clamp-4">
                                <MarkdownWord content={notif.message} />
                              </div>

                              <div className="flex items-center space-x-2 mt-2">
                                <Icon name="Clock" size={12} color="#94A3B8" />
                                <span className="text-xs text-secondary-400">
                                  {getTimeAgo(notif.created_at)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-start space-x-1">
                              {!notif.is_read && (
                                <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                              )}
                              <button
                                className={`delete-button p-1.5 rounded-lg transition-all duration-200
                                  ${hoveredNotificationId === notif.id ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
                                  hover:bg-error-50 group/delete`}
                                onClick={(e) => handleDeleteNotification(notif.id, e)}
                                title="Remove notification"
                              >
                                <Icon
                                  name="X"
                                  size={16}
                                  className="text-secondary-400 group-hover/delete:text-error transition-colors"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSystem;
